import { getSupabase } from '@/lib/supabase';
import type { TeamPreset } from '@/schemas';
import type { UserId } from '@/schemas/ids';
import type { RealtimeSubscription } from '../realtime';
import { decodeRowToTeamPreset, type TeamPresetRow } from '../sync/codec';

export type TeamPresetChangeType = 'INSERT' | 'UPDATE' | 'DELETE';

export interface TeamPresetChange {
  type: TeamPresetChangeType;
  preset: TeamPreset | null; // null for DELETE
}

export async function subscribeToTeamPresets(
  userId: UserId,
  onChange: (change: TeamPresetChange) => void,
): Promise<RealtimeSubscription> {
  const client = getSupabase();
  const {
    data: { session },
  } = await client.auth.getSession();
  if (!session?.user) {
    throw new Error('User must be authenticated to subscribe to team presets');
  }

  const channel = client.channel(`team-presets:${userId}`);

  // TypeScript has issues with multiple .on() calls, so we use type assertion
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment -- Supabase types don't properly support chained .on() calls
  const typedChannel = channel as any;

  // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- Supabase types don't properly support chained .on() calls
  typedChannel.on(
    'postgres_changes',
    {
      event: 'INSERT',
      schema: 'public',
      table: 'team_presets',
      filter: `user_id=eq.${userId}`,
    },
    (payload: { new: TeamPresetRow }) => {
      try {
        const preset = decodeRowToTeamPreset(payload.new);

        onChange({ type: 'INSERT', preset });
      } catch (error: unknown) {
        const err = error instanceof Error ? error : new Error(String(error));
        console.warn('Failed to decode realtime team preset (INSERT)', err);
      }
    },
  );

  // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- Supabase types don't properly support chained .on() calls
  typedChannel.on(
    'postgres_changes',
    {
      event: 'UPDATE',
      schema: 'public',
      table: 'team_presets',
      filter: `user_id=eq.${userId}`,
    },
    (payload: { new: TeamPresetRow }) => {
      try {
        const preset = decodeRowToTeamPreset(payload.new);

        onChange({ type: 'UPDATE', preset });
      } catch (error: unknown) {
        const err = error instanceof Error ? error : new Error(String(error));
        console.warn('Failed to decode realtime team preset (UPDATE)', err);
      }
    },
  );

  // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- Supabase types don't properly support chained .on() calls
  typedChannel.on(
    'postgres_changes',
    {
      event: 'DELETE',
      schema: 'public',
      table: 'team_presets',
      filter: `user_id=eq.${userId}`,
    },
    (payload: { old: TeamPresetRow }) => {
      try {
        const preset = decodeRowToTeamPreset(payload.old);

        onChange({ type: 'DELETE', preset });
      } catch (error: unknown) {
        const err = error instanceof Error ? error : new Error(String(error));
        console.warn('Failed to decode realtime team preset (DELETE)', err);
      }
    },
  );

  channel.subscribe();
  return {
    unsubscribe: () => {
      void client.removeChannel(channel);
    },
  };
}
